import React, { useState, useEffect, useRef } from "react";
import "./MembershipRetentionRateReportView.scss";
import DataLoader from "../../components/ui/dataLoader/DataLoader";
import ToastService from "../../services/toastService/ToastService";
import { useGetMembershipRetentionReportMutation } from "../../app/services/Resource/resourceApi";

const MembershipRetentionRateReportView = () => {
    const currentYear = new Date().getFullYear();
    const startYear = 2010;
    const endYear = Math.max(currentYear , 2026);

    const years = [];
    for (let y = endYear; y >= startYear; y--) {
        years.push(y);
    }

    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [report, setReport] = useState(null);
    const dropdownRef = useRef(null);

    const [getMembershipRetentionReport, { isSuccess, isLoading, isError, data }] = useGetMembershipRetentionReportMutation();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    useEffect(() => {
        setReport(null);
        getMembershipRetentionReport({ ReportYear: selectedYear });
    }, [selectedYear]);

    useEffect(() => {
        if (isSuccess && !isLoading && data) {
            setReport(Array.isArray(data) ? data[0] ?? null : data);
        }
    }, [isSuccess, isLoading, data]);

    useEffect(() => {
        if (isError) {
            ToastService.error("Failed to load the retention report. Please try again.");
        }
    }, [isError]);

    const handleSelectYear = (year) => {
        setSelectedYear(year);
        setIsDropdownOpen(false);
    };

    const fmt = (v) => (v === null || v === undefined ? "-" : v);
    const rateText = report?.retentionRate !== null && report?.retentionRate !== undefined ? `${Number(report.retentionRate).toFixed(2)}%` : "-";
    const renewalDue = Number(report?.renewalDue || 0);
    const renewed    = Number(report?.renewedMembers || 0);
    const pendingPay = Number(report?.pendingPayment || 0);
    const doNotWish  = Number(report?.doNotWishToRenew || 0);

    const pendingResponse = doNotWish;

    const optOutDisplay = Math.max(0, renewalDue - renewed - pendingPay - pendingResponse);

    return (
        <div className="retention-report-page-wrapper">
            <div className="retention-card">
                <div className="table-responsive">
                    <table className="retention-report-table-full">
                        <tbody>
                            {/* Report Generated Year Header Row */}
                            <tr className="main-header-row">
                                <td className="header-title-cell">
                                    <span>Report Generated Year</span>
                                </td>
                                <td className="header-filter-cell">
                                    <div className="custom-year-dropdown" ref={dropdownRef}>
                                        <button
                                            type="button"
                                            className={`dropdown-trigger-btn ${isDropdownOpen ? "active" : ""}`}
                                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                            aria-haspopup="listbox"
                                            aria-expanded={isDropdownOpen}
                                        >
                                            <span className="selected-year-text">{selectedYear}</span>
                                            <i className={`bi bi-chevron-down dropdown-arrow ${isDropdownOpen ? "rotate" : ""}`}></i>
                                        </button>

                                        {isDropdownOpen && (
                                            <ul className="dropdown-menu-list" role="listbox">
                                                {years.map((year) => {
                                                    const isSelected = year === selectedYear;
                                                    return (
                                                        <li
                                                            key={year}
                                                            className={`dropdown-item ${isSelected ? "selected" : ""}`}
                                                            onClick={() => handleSelectYear(year)}
                                                            role="option"
                                                            aria-selected={isSelected}
                                                        >
                                                            <span className="year-number">{year}</span>
                                                            {isSelected && (
                                                                <i className="bi bi-check2 check-icon"></i>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </div>
                                </td>
                            </tr>

                            {/* Loader stays inside the card so the year dropdown never unmounts */}
                            {isLoading && (
                                <tr className="data-row">
                                    <td className="label-col" colSpan="2">
                                        <DataLoader />
                                    </td>
                                </tr>
                            )}

                            {!isLoading && !report && (
                                <tr className="data-row">
                                    <td className="label-col" colSpan="2">
                                        No report data available for {selectedYear - 1}.
                                    </td>
                                </tr>
                            )}
                           {!isLoading && report && (
                                <>
                                    {/* Section A: Retention Rate */}
                                    <tr className="section-header-row">
                                        <td colSpan="2">
                                            A. Retention Rate for {selectedYear - 1}
                                        </td>
                                    </tr>

                                    <tr className="data-row">
                                        <td className="label-col">
                                            Total active members as of {selectedYear - 2}
                                        </td>
                                        <td className="value-col">
                                            {fmt(report.previousYearTotal)}
                                        </td>
                                    </tr>

                                    <tr className="data-row">
                                        <td className="label-col">
                                            Members retained in {selectedYear - 1}
                                        </td>
                                        <td className="value-col">
                                            {fmt(report.retainedMembers)}
                                        </td>
                                    </tr>

                                    <tr className="data-row rate-highlight-row">
                                        <td className="label-col">
                                            <strong>Retention Rate</strong>
                                        </td>
                                        <td className="value-col">
                                            <span className="rate-badge">{rateText}</span>
                                        </td>
                                    </tr>

                                    {/* Section B: Renewal Cycle */}
                                    <tr className="section-header-row">
                                        <td colSpan="2">
                                            B. Renewal Cycle for {selectedYear - 1}
                                        </td>
                                    </tr>

                                    <tr className="data-row row-parent">
                                        <td className="label-col font-bold">
                                            Members pending renewal response in {selectedYear - 1}
                                        </td>
                                        <td className="value-col font-bold">
                                            {fmt(pendingResponse)}
                                        </td>
                                    </tr>

                                    <tr className="data-row row-child">
                                        <td className="label-col sub-level">
                                            Do not wish to renew ({selectedYear - 1})
                                        </td>
                                        <td className="value-col">
                                            {fmt(report.doNotWishToRenew)}
                                        </td>
                                    </tr>

                                    <tr className="data-row row-child">
                                        <td className="label-col sub-level">
                                            Unsure if want to renew ({selectedYear - 1})
                                        </td>
                                        <td className="value-col">
                                            -
                                        </td>
                                    </tr>

                                    <tr className="data-row row-parent">
                                        <td className="label-col font-bold">
                                            Members renewed in {selectedYear - 1}
                                        </td>
                                        <td className="value-col font-bold">
                                            {fmt(renewed + pendingPay + optOutDisplay)}
                                        </td>
                                    </tr>

                                    <tr className="data-row row-child">
                                        <td className="label-col sub-level">
                                            Renewed (For members who joined in {selectedYear - 1})
                                        </td>
                                        <td className="value-col">
                                            {fmt(report.renewedMembers)}
                                        </td>
                                    </tr>

                                    <tr className="data-row row-child">
                                        <td className="label-col sub-level">
                                            Pending payment in {selectedYear - 1}
                                        </td>
                                        <td className="value-col">
                                            {fmt(report.pendingPayment)}
                                        </td>
                                    </tr>

                                    <tr className="data-row row-child">
                                        <td className="label-col sub-level">
                                            Opt-out Expiry/Termination
                                        </td>
                                        <td className="value-col">
                                            {fmt(optOutDisplay)}
                                        </td>
                                    </tr>
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default MembershipRetentionRateReportView;